<!--
  Golem fork: OUR coin.

  The square is a clock, so the seat is a WHEEL. The role token is drawn on
  our own cut gear (`token-golem.png`, baked by
  claude_temp_test/2026-08-18-token-coin.mjs) instead of upstream's marbled
  `token.png` — a finely milled iron rim, a tarnished collar, and a recessed
  parchment face, all of it worn and lit from one origin — and every
  decoration on it is drawn by this component rather than pulled from
  upstream's leaf art.

  What the coin has to say, and how it says it:
    · the role            — the engraved icon, centred on the face
    · the role's name     — cut along the bottom arc, centred on it
    · acts on night one   — a waxing crescent at 9 o'clock
    · acts on later nights— a waning crescent at 3 o'clock
    · how many reminders  — that many bone studs across the top of the wheel
    · changes setup       — a red stone set in bone, upper right
    · which team          — a whisper of team colour in the collar

  Nothing on the coin wears a traced outline of constant width: the marks are
  seated with shadow and the name is cut into the parchment with a lit lower
  lip, because a uniform stroke is what made the first passes read as vector
  art next to the painted role icons.

  Upstream's `token.png` and `leaf-*.png` stay in the tree, unreferenced.
-->
<template>
  <div
    class="token"
    @click="setRole"
    :class="[role.id, { 'seat-frame-tarot': isTarot }]"
    :aria-label="spokenRole"
    @mouseenter="showCard"
    @mouseleave="hideCard"
  >
    <span class="rim" :class="role.team"></span>
    <!-- FT-1446 (user): an open seat's chair, drawn INSIDE the coin so it sits
         behind the role art (the storyteller sees a role on an unclaimed seat;
         a chair drawn over the whole coin covered it). -->
    <span class="seat-chair" v-if="chair" aria-hidden="true"></span>
    <!-- FT-985: the art is wrapped so the COIN'S OWN WIDTH can be queried (see
         `.icon-fit` in the styles). The wrapper is what carries the container,
         never `.token` itself: containment would re-root the `position: fixed`
         RoleHoverCard that the bluffs and both pickers render inside this same
         root, and the card measures itself against the viewport. -->
    <span class="icon-fit" v-if="role.id">
      <span class="icon" :style="iconStyle"></span>
    </span>

    <!-- night order, reminder count and setup, set into the wheel -->
    <svg viewBox="0 0 150 150" class="marks" v-if="hasMarks">
      <!-- ONE phase at top centre says when this character wakes (user call
           2026-08-18): a crescent for the first night, the rest of that moon
           for later nights, the full moon for both. It takes the spot the
           reminder studs held — the studs' computed stays below, unused. -->
      <image
        v-if="nightPhase"
        class="mark phase"
        :href="nightPhase"
        :x="PHASE_X"
        :y="PHASE_Y"
        :width="PHASE_W"
        :height="PHASE_W"
      />
      <template v-if="role.setup">
        <path class="mark setting" :d="setupLozenge.bezel" />
        <path class="mark stone" :d="setupLozenge.stone" />
      </template>
    </svg>

    <svg viewBox="0 0 150 150" class="name">
      <path :d="nameCurve" :id="curveId" fill="transparent" />
      <text
        class="label"
        text-anchor="middle"
        width="150"
        :font-size="role.name | nameToFontSize"
      ><!-- startOffset centres the name on the ARC. Upstream anchored with
             x="66.6%" — 100 user units, half of their much deeper curve but
             two thirds along ours, so short names sat off to one side and
             long ones ran off the end and were clipped. --><textPath
          :xlink:href="'#' + curveId"
          startOffset="50%"
        >{{ role.name }}</textPath></text>
    </svg>

    <!-- FT-1080: STOOD DOWN, NOT REMOVED. Nothing passes `belief` any more —
         Player.vue docks the chip beside the coin instead, because a chip
         inside `.token` cannot out-rank the seat's `.shroud`: `.token` opens a
         stacking context (`transform` in Player.vue, `filter` here), so the
         `z-index: 4` below is spent inside the coin and the shroud swallowed
         the pointer over the chip's on-coin half. Everything here still works
         the moment a consumer passes `belief` again; the seat is simply not
         that consumer. See Player.vue's `.belief-dock` for the whole note. -->
    <!-- FT-861: THE BELIEF CHIP — what this seat's player was TOLD they are,
         pinned to the coin's bottom edge (the moon took the top). The truth
         stays the whole coin; the lie is a chip on its rim, which is the right
         proportion: the storyteller reads the character that resolves first and
         the performance second. Only a SEAT passes `belief`; the bluffs, the
         fabled and both pickers never do, so nothing else grows one. -->
    <button
      v-if="belief && (belief.id || belief.placeholder)"
      type="button"
      class="belief-chip"
      :class="[belief.team, beliefSide === 'left' ? 'chip-left' : 'chip-right']"
      :title="belief.placeholder ? 'This character believes it is something else — click to set what they were told' : `Believes they are the ${belief.name} — click to change what they were told`"
      :aria-label="belief.placeholder ? 'Set what they believe they are' : `Believes they are the ${belief.name}`"
      @click.stop="$emit('set-belief')"
      @mouseenter="$emit('belief-hover', $event)"
      @mouseleave="$emit('belief-leave')"
    >
      <span
        class="belief-icon"
        :class="{ unset: belief.placeholder }"
        :style="belief.placeholder ? null : { backgroundImage: `url(${beliefIcon})` }"
        >{{ belief.placeholder ? "?" : "" }}</span
      >
    </button>

    <!-- FT-858: the coin's read is THE role hover card — the same component
         the Almanac workbench's shelf hovers (user-directed: one component,
         both surfaces). It supersedes the flat `.ability` box that used to
         hang off the coin at `left: 120%`, which the square's rotated seats
         clipped and the picker grids overlapped. -->
    <RoleHoverCard
      v-if="cardAnchor"
      :role="role"
      :anchor="cardAnchor"
      @dismiss="hideCard"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
// FT-858: THE role hover card, shared with the Almanac workbench's shelf and
// the grimoire drawer.
import RoleHoverCard from "./RoleHoverCard";
import moonFirst from "../assets/moon-first.png";
import moonOther from "../assets/moon-other.png";
import moonFull from "../assets/moon-full.png";
// FT-887: the coin's moon and the hover card's chip make the SAME claim, so
// they read the same function — see golem/nightInfo's wakesOn().
import { wakesOn } from "../golem/nightInfo";
// FT-1469: which frame the seats wear this browser (coin lab's twin) — the
// coin binds `seat-frame-tarot` from it so the card is a repaint.
import { frameChoice } from "../golem/seatFrame";

// how long the cursor has to rest on a coin before its card appears —
// enough that sweeping across the square does not strobe cards
const HOVER_DELAY = 170;
// the 150-unit space every overlay on the coin is drawn in
const CX = 75;
const CY = 75;
// Where the wheel's decorations sit, measured from the middle of the coin.
// The coin is the USER'S OWN art (design drop, keyed + measured by
// claude_temp_test/2026-08-18-player-coin.mjs). Its metal ring is thin — the
// parchment runs out to 0.955 of the radius and the teeth take the rest — so
// the marks sit just INSIDE the gold hairline rather than in the ring, which
// is where they fit and where upstream's leaves read from.
const MARK_R = 67.5;
// the phase mark, centred on the top of the wheel just inside the hairline.
// 15 read as a smudge at seat sizes (user report 2026-08-18) -- 21 is the
// biggest step that still clears the parchment edge at this anchor; PHASE_X/
// PHASE_Y grow around the same MARK_R centre, so raising this one constant
// is the whole resize.
const PHASE_W = 21;
const PHASE_X = (CX - PHASE_W / 2).toFixed(2);
const PHASE_Y = (CY - MARK_R - PHASE_W / 2 + 2).toFixed(2);
const RAD = Math.PI / 180;

/**
 * A crescent: the lune left when a bite circle is pushed into a disc. The
 * two arcs meet at the circles' real intersection points, so the horns come
 * to a proper point instead of the blunt stubs a hand-guessed path gives.
 *
 * @param {number} deg  where on the wheel the mark sits (0 = 3 o'clock)
 * @param {number} turn rotation of the mark itself — which way the horns face
 */
function crescent(deg, turn) {
  const R = 5; // the disc
  const r = 4.5; // the bite
  const o = 2.4; // how deep the bite goes
  const ix = (o * o + R * R - r * r) / (2 * o);
  const iy = Math.sqrt(Math.max(0, R * R - ix * ix));
  const cx = CX + MARK_R * Math.cos(deg * RAD);
  const cy = CY + MARK_R * Math.sin(deg * RAD);
  const c = Math.cos(turn * RAD);
  const s = Math.sin(turn * RAD);
  const at = (px, py) =>
    `${(cx + px * c - py * s).toFixed(2)} ${(cy + px * s + py * c).toFixed(2)}`;
  // outer arc the long way round the disc, then back along the bite
  return (
    `M ${at(ix, -iy)} A ${R} ${R} 0 1 0 ${at(ix, iy)}` +
    ` A ${r} ${r} 0 0 1 ${at(ix, -iy)} Z`
  );
}

/** a diamond of half-height `s` about (x, y) */
function lozenge(x, y, s) {
  return (
    `M ${x.toFixed(2)} ${(y - s).toFixed(2)}` +
    ` L ${(x + s * 0.78).toFixed(2)} ${y.toFixed(2)}` +
    ` L ${x.toFixed(2)} ${(y + s).toFixed(2)}` +
    ` L ${(x - s * 0.78).toFixed(2)} ${y.toFixed(2)} Z`
  );
}

export default {
  components: { RoleHoverCard },
  props: {
    role: {
      type: Object,
      default: () => ({})
    },
    /** FT-1446: paint the open-seat chair behind the role art. */
    chair: {
      type: Boolean,
      default: false
    },
    /**
     * Whether the coin raises its own hover card. True everywhere the coin is
     * the whole target — the bluffs, the fabled, both role pickers. A SEAT
     * turns it off: the shroud and the life token cover the coin's top half,
     * so the seat owns the hover for its whole box instead (Player.vue).
     */
    /**
     * FT-1079b (user): which side of the coin the belief chip hangs half off.
     * "left" for seats at 12, 6 and the board's left arc; "right" otherwise.
     * The seat computes it (Player.vue owns the ring geometry).
     */
    beliefSide: {
      type: String,
      default: "right"
    },
    hoverCard: {
      type: Boolean,
      default: true
    },
    /**
     * FT-861: the character this seat's player was TOLD they are, when it is
     * not the one on the coin. Null on every coin that is not a storyteller's
     * seat — see Player.vue, which is the only caller that passes it and which
     * gates it on being the storyteller's own grimoire view.
     */
    belief: {
      type: Object,
      default: null
    }
  },
  computed: {
    /** FT-1469: is this browser wearing the tarot-card frame? Drives the
     *  `seat-frame-tarot` hook on the coin's own root. */
    isTarot: function() {
      return frameChoice.id === "tarot";
    },
    /**
     * When this character wakes, as one moon: a crescent for the first night,
     * the rest of that moon for later nights, the full moon for both. (user
     * call 2026-08-18 — it took the reminder studs' place at top centre.)
     */
    nightPhase: function() {
      const { first, other } = wakesOn(this.role);
      if (first && other) return moonFull;
      if (first) return moonFirst;
      if (other) return moonOther;
      return null;
    },
    /**
     * How many reminder tokens this role puts on the board — the count the
     * studs across the top of the wheel report.
     */
    reminderLeaves() {
      const { reminders, remindersGlobal } = this.role;
      return [reminders, remindersGlobal].reduce(
        (total, list) => total + (list ? list.length : 0),
        0,
      );
    },
    /**
     * What a screen reader hears. The hover card is a pointer affordance, and
     * the ability text it carries used to be real text on the coin — this is
     * where that reading goes instead, so nothing is lost off the mouse path.
     */
    spokenRole: function() {
      if (!this.role || !this.role.name) return null;
      return this.role.ability
        ? `${this.role.name}. ${this.role.ability}`
        : this.role.name;
    },
    /** Every instance needs its own curve id, or they all share the first. */
    curveId: function() {
      return `coin-curve-${this._uid}`;
    },
    hasMarks: function() {
      // the moon is one of the marks, so this asks the same question it does
      const { first, other } = wakesOn(this.role);
      return !!(first || other || this.role.setup || this.reminderLeaves);
    },
    /** Acts on the first night — a waxing crescent at 9 o'clock. */
    firstNightMoon: function() {
      return crescent(180, 0);
    },
    /** Acts on later nights — the same moon, waning, at 3 o'clock. */
    otherNightMoon: function() {
      return crescent(0, 180);
    },
    /** One stud per reminder token, centred on the top of the wheel. */
    reminderStuds: function() {
      const n = Math.min(5, this.reminderLeaves);
      const studs = [];
      for (let i = 0; i < n; i++) {
        const deg = -90 + (i - (n - 1) / 2) * 10;
        studs.push({
          x: (CX + MARK_R * Math.cos(deg * RAD)).toFixed(2),
          y: (CY + MARK_R * Math.sin(deg * RAD)).toFixed(2)
        });
      }
      return studs;
    },
    /**
     * Changes the setup — a red stone set in a bone bezel, upper right. Two
     * shapes rather than a stroked one: the bezel IS the outline, and it
     * varies the way a setting does instead of tracing at a fixed width.
     */
    setupLozenge: function() {
      const deg = -46;
      const x = CX + MARK_R * Math.cos(deg * RAD);
      const y = CY + MARK_R * Math.sin(deg * RAD);
      return { bezel: lozenge(x, y, 4.6), stone: lozenge(x, y + 0.2, 2.7) };
    },
    /**
     * The arc the name is cut along, symmetric about the coin's vertical
     * axis so a 50% startOffset lands exactly on bottom-centre. Descenders
     * reach about 60 units from the middle; the parchment ends at 63.6.
     */
    nameCurve: function() {
      // FT-861: with a belief chip pinned to the bottom edge the name rises
      // out of its way. The truth's name stays whole and legible — that is the
      // one thing on this coin that is not tradeable for the chip.
      if (this.belief && this.belief.id) {
        return "M 25 70 C 25 130, 125 130, 125 70";
      }
      return "M 25 81 C 25 147, 125 147, 125 81";
    },
    /**
     * FT-985: the engraved art's own url. Lifted out of the template — where
     * it was an inline conditional inside the style binding — when the art
     * gained its `.icon-fit` wrapper, so the expression is not re-indented
     * every time the markup around it moves. `beliefIcon` below already
     * answers the same question this way for the chip.
     *
     * The choice it makes is unchanged: the role's OWN image when remote art
     * is opted in (or when it is already inline data, which needs no opt-in
     * because nothing is fetched), and the bundled icon otherwise.
     */
    roleIcon() {
      const role = this.role;
      if (!role || !role.id) return "";
      if (
        role.image &&
        (this.grimoire.isImageOptIn || role.image.startsWith("data:"))
      ) {
        return role.image;
      }
      return require("../assets/icons/" + (role.imageAlt || role.id) + ".png");
    },
    /**
     * FT-1042: the art's FIT — how the forge placed this role's engraving on
     * the coin. Three optional fields ride the role (snapshot semantics,
     * like the baked icon): golemArtScale (0.4–2, default 1) and
     * golemArtX/golemArtY (percent of the coin's width, ±50, default 0).
     * Percent units, so every coin — seat, bluff, picker, forge preview —
     * renders the same fit at its own size. Clamped HERE, at the render,
     * so a hand-edited or imported role can never fling the art off-coin.
     *
     * Null when the role carries no adjustment: the style object then has
     * no transform at all, and every existing role renders exactly as it
     * always did (that absence is a hard requirement, not an optimization).
     */
    artTransform() {
      const r = this.role || {};
      const num = (v, d) => (typeof v === "number" && isFinite(v) ? v : d);
      const s = Math.min(2, Math.max(0.4, num(r.golemArtScale, 1)));
      const x = Math.min(50, Math.max(-50, num(r.golemArtX, 0)));
      const y = Math.min(50, Math.max(-50, num(r.golemArtY, 0)));
      if (s === 1 && x === 0 && y === 0) return null;
      return `translate(${x}%, ${y}%) scale(${s})`;
    },
    /** The engraved art's inline style — the image, plus the FT-1042 fit
     *  when the role carries one. */
    iconStyle() {
      const style = { backgroundImage: `url(${this.roleIcon})` };
      if (this.artTransform) style.transform = this.artTransform;
      return style;
    },
    /**
     * FT-861: the believed character's engraved art, for the chip. Bundled
     * icons only (a chip is 30px — a remote image would be a smudge and would
     * need the opt-in), falling back the way the night sheet's rows do.
     */
    beliefIcon: function() {
      const role = this.belief;
      if (!role || !role.id) return "";
      if (role.golemIconData) return role.golemIconData;
      try {
        return require("../assets/icons/" + (role.imageAlt || role.id) + ".png");
      } catch (e) {
        return require("../assets/icons/custom.png");
      }
    },
    ...mapState(["grimoire"])
  },
  data() {
    return {
      // the coin element the hover card is pinned to, once the cursor has
      // rested on it; null while nothing is showing
      cardAnchor: null,
      // the phase mark's geometry, exposed for the template
      PHASE_W,
      PHASE_X,
      PHASE_Y
    };
  },
  beforeDestroy() {
    clearTimeout(this.$options.cardTimer);
  },
  filters: {
    /**
     * The wheel's face is smaller than upstream's full-bleed disc, so its
     * name arc is shorter and one long/short pair of sizes no longer covers
     * the range. Calibrated against measured glyph widths on 154 units of
     * arc: the longest name in any script ("Devil's Advocate") still lands
     * inside it.
     */
    nameToFontSize: name => {
      const n = (name || "").length;
      if (n <= 5) return "104%";
      if (n <= 8) return "96%";
      if (n <= 11) return "86%";
      if (n <= 14) return "74%";
      if (n <= 17) return "64%";
      return "56%";
    }
  },
  methods: {
    setRole() {
      this.$emit("set-role");
    },
    /**
     * Rest the cursor on a coin and it tells you what the character does.
     * An empty chair has nothing to say, and a touch screen has no hover to
     * rest — a tap there still opens the role picker, as it always did.
     */
    showCard(e) {
      if (!this.hoverCard) return;
      if (!this.role || !this.role.id) return;
      if (!window.matchMedia("(hover: hover)").matches) return;
      const el = e.currentTarget;
      clearTimeout(this.$options.cardTimer);
      this.$options.cardTimer = setTimeout(() => {
        this.cardAnchor = el;
      }, HOVER_DELAY);
    },
    hideCard() {
      clearTimeout(this.$options.cardTimer);
      this.cardAnchor = null;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../vars.scss";

$ink: #241a10; // the name, cut into the parchment
$lip: rgba(246, 232, 200, 0.72); // the lit lower edge of that cut
$bone: #ded0ac; // rim marks: bone set into the iron
$blood: #970000; // our red, for the one mark that must not be missed

.token {
  width: 100%;
  // OUR coin. Upstream's token.png stays in the tree, unreferenced.
  // no-repeat: the shorthand resets background-repeat, and a tiled coin put
  // the NEXT tile's top edge just below this one — the "melting" rim, the
  // stray red nub and the thing peeking from behind the disc were all one
  // bug (user diagnosis 2026-08-18)
  background: var(--coin, url("../assets/token-golem.png")) center center /
      contain no-repeat;
  text-align: center;
  // NO circular clip: the coin is a TOOTHED wheel, and border-radius: 50%
  // cut every tooth that crossed the inscribed circle (user report
  // 2026-08-18 — the coins looked clipped). The art carries its own edge.
  // The border stays — the bluffs collapse zeroes its width and the "you"
  // seat's glow animates its colour — but it goes transparent at rest: a
  // black hoop drawn round a toothed wheel closes the silhouette back into
  // a circle and throws the gear away.
  border: 3px solid transparent;
  // A box-shadow follows the BOX (a circle, here) and so drew a dark ring
  // that showed past the coin's teeth — the "something peeking from behind
  // the disc" (user report 2026-08-18). drop-shadow follows the art's own
  // alpha, teeth included.
  filter: drop-shadow(0 0 7px rgba(0, 0, 0, 0.55));
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: border-color 0.25s;

  &:hover .name .label {
    fill: $blood;
  }
  // the fabled remove-X rides `:before` from TownSquare.vue, so its box has
  // to keep upstream's full-bleed geometry
  &:before {
    position: absolute;
    inset-block-start: 3%;
    inset-inline-start: 0;
    inline-size: 100%;
    block-size: 100%;
    background: no-repeat center 30% / 100% auto;
  }
  span {
    position: absolute;
    inline-size: 100%;
    block-size: 100%;
    pointer-events: none;
    background-size: 100%;
  }
  // The engraved role art. The box is the whole coin, so `center` is the
  // coin's true centre on both axes — the art is placed purely by
  // background-size/position and carries no margin to drift on.
  //   width  = 76% of the coin, so it is centred exactly, with the same
  //            12% of coin either side before the parchment ends at 15.2%
  //   height = the same 76%, dropped 40% of the way through the 24% of
  //            slack, which lands its centre at 47.6% — a deliberate 2.4%
  //            lift to make room for the name on the bottom arc
  .icon {
    // Bigger, and sitting nearer the middle of the face (user call
    // 2026-08-18). 76% at 40% left the character small and riding high, with
    // dead parchment under it. The name curves along an arc that starts at
    // y=81 of 150 and bottoms around y=130, so the icon cannot simply centre
    // at 50% without its lower edge crossing the lettering — 45% is the
    // furthest down it goes while the art still clears the name.
    // FT-985: PINNED, not statically placed. `.token` is a centring flex box,
    // and this layer used to get its horizontal placement from that centring
    // alone (no `left`). Wrapped in `.icon-fit` it stopped being the flex
    // box's own child and the static position resolved half a coin to the
    // right — measured at 839.4px vs the wrapper's 898.4px on a 124px coin,
    // which is the whole art shifted off the face. An explicit origin makes
    // the placement the wrapper's, not the flex box's leftovers.
    left: 0;
    top: 0;
    background-size: 86%;
    background-repeat: no-repeat;
    background-position: center 45%;
  }
  // FT-985 — THE ENGRAVED ART IN A CROWDED TOWN.
  //
  // The role art is bright blue line work on a mid-tone gold face, and it
  // carries no dark edge of its own. At a roomy seat that is fine; pack the
  // ring and the strokes thin out until the whole coin reads as a pale blue
  // smudge (user report: "a bit white and hard to identify at this size").
  //
  // MEASURED, at the real render size, not argued — seat 1's Washerwoman,
  // 1280x800, over the parchment face only (the iron rim is high-contrast
  // metal and would drown the thing being measured):
  //
  //                              value range (p95-p5)   edge energy
  //   96px coin, as shipped ....... 117.8 ................ 111.8
  //   + dark halo alone ........... 127.8 ................ 123.1
  //   + contrast/saturation alone . 126.9 ................ 118.7
  //   + both (shipped here) ....... 135.5 ................ 128.4   +15% / +15%
  //
  // A slight scale-up measured well too (+4% edge at 94%) and is NOT taken:
  // the art already sits as low as it can go before crossing the name arc
  // (see `.icon` above), and growing it spends that clearance for the
  // smallest of the three gains.
  //
  // THE HALO IS THE LOAD-BEARING HALF, and it is the coin's own idiom — the
  // moon, the cut name and the vote marks are all seated the same way, each
  // against its own opposite. `$ink` is the dark this coin already uses.
  //
  // WHY A CONTAINER, AND WHY vmin: a fixed-pixel threshold would key off the
  // monitor as much as the town — the same 15-seat ring is a 96px coin at
  // 1280x800 and a 130px coin at 1920x1080, and it is the CROWD, not the
  // display, that the user is complaining about. Every seat is sized as a
  // fraction of the window's short side (Player.vue's `zoom`: 15.5 / 13.5 /
  // 12 / 10.5 vmin as the town grows), so a vmin threshold cuts exactly
  // between "a roomy ring" and "a crowded one" on every screen. 12.5vmin
  // catches the 12vmin (11-15 seat) and 10.5vmin (16+ seat) rings and leaves
  // the two roomier ones untouched — not softened, IDENTICAL: there is no
  // rule for them to match.
  // FT-1446: the open-seat chair behind the role art — the resting chair's
  // recipe (Player.vue .open-mark): the chair lab's art, ink, opacity and
  // coin size dials, centred on the coin's face.
  .seat-chair {
    color: var(--chair-ink, #9a9285);
    opacity: var(--chair-opacity, 1);
    filter: drop-shadow(0 0 3px black);
    &::before {
      content: "";
      display: block;
      width: 100%;
      height: 100%;
      background-color: currentColor;
      -webkit-mask: var(--chair, url("../assets/ui-seat-front.svg")) center /
        auto calc(26% * var(--chair-size-coin, 1)) no-repeat;
      mask: var(--chair, url("../assets/ui-seat-front.svg")) center /
        auto calc(26% * var(--chair-size-coin, 1)) no-repeat;
    }
  }
  .icon-fit {
    container: coin / inline-size;

    @container coin (max-width: 12.5vmin) {
      .icon {
        filter: drop-shadow(0 0 1.2px rgba($ink, 0.9))
          drop-shadow(0 0 0.6px rgba($ink, 0.9))
          drop-shadow(0 0 0.6px rgba($ink, 0.9)) contrast(1.18) saturate(1.18);
      }
    }
  }

  // `closest-side` makes the stops read as a fraction of the coin's radius,
  // so the band lands on the collar at every zoom
  @mixin team-ring($color) {
    background: radial-gradient(
      circle closest-side,
      transparent 0 var(--ring-in, 87.5%),
      rgba($color, 0.55) var(--ring-a, 89%),
      rgba($color, 0.55) var(--ring-b, 92%),
      transparent var(--ring-out, 93.5%)
    );
  }
  .rim {
    border-radius: 50%;
    background-size: 100% 100%;
    mix-blend-mode: soft-light;
    &.townsfolk {
      @include team-ring($townsfolk);
    }
    &.outsider {
      @include team-ring($outsider);
    }
    &.minion {
      @include team-ring($minion);
    }
    &.demon {
      @include team-ring($demon);
    }
    &.traveler {
      @include team-ring($traveler);
    }
  }
  // night order / reminder count / setup. No stroke: on dark iron the bone
  // has all the contrast it needs, and a shadow seats it the way a real
  // inlay sits in its recess.
  .marks {
    position: absolute;
    width: 100%;
    height: 100%;
    pointer-events: none;
    filter: drop-shadow(0.4px 0.9px 0.7px rgba(0, 0, 0, 0.85));

    .moon,
    .stud,
    .setting {
      fill: $bone;
    }
    .stone {
      fill: $blood;
    }

    // the moon PNG bakes its own halo, but on dark iron it still wants a
    // second lift: a warm glow stacked outside the parent's dark contact
    // shadow above, the same two-tier trick the name halo below uses.
    .mark.phase {
      filter: drop-shadow(0 0 2.6px rgba(250, 240, 210, 0.85))
        drop-shadow(0 0 1.2px rgba(250, 240, 210, 0.9));
    }
  }
  .name {
    width: 100%;
    height: 100%;
    font-size: 24px;

    .label {
      fill: $ink;
      // Cut, not stickered. Stacked blurred blooms give a halo that follows
      // the glyph and fades — legible on the aged face without the 2.5px
      // paint-order outline the earlier passes wore, which was a traced ring
      // of constant width and the loudest "vector" tell on the coin. The
      // last shadow is the lit lower lip of the cut.
      // The halo is BACK (user call 2026-08-18): it went when the coin was
      // pale, and the darker coin art needs it again for the name to read.
      filter: drop-shadow(0 0 1.6px rgba(250, 240, 214, 0.95))
        drop-shadow(0 0 0.9px rgba(250, 240, 214, 0.95))
        drop-shadow(0 0 0.6px rgba(250, 240, 214, 0.8))
        drop-shadow(0 0.9px 0 #{$lip});
      // PiratesBay is what the rest of our chrome is lettered in; upstream's
      // Papyrus stays in assets/fonts, unreferenced
      font-family: "PiratesBay", Georgia, serif;
      letter-spacing: 0.5px;
      transition: fill 200ms;
    }
  }
  // FT-861: THE BELIEF CHIP. A smaller coin struck in the same metal, set into
  // the wheel's bottom edge — half on the rim, half proud of it, so it reads as
  // pinned TO the coin rather than as part of the face. The team's colour is a
  // hairline on its collar, the same whisper the big coin's rim carries.
  //
  // FT-1080: unreferenced while the seat docks its own chip (see the template
  // note above and Player.vue's `.belief-dock`). This block is `scoped`, so it
  // could never have styled markup Player.vue renders — the seat carries its
  // own copy of these declarations, and this one waits here for the day the
  // coin wears a chip again.
  .belief-chip {
    position: absolute;
    // FT-1021: ~50% bigger than the bluff minis, overlapping the coin.
    // FT-1079b (user): the chip hangs HALF OFF THE COIN'S SIDE — centred it
    // covered the art and the name. The side is the seat's own place on the
    // ring (12, 6 and the left arc hang LEFT; every other seat hangs RIGHT),
    // so the chip always leans toward the board's outside.
    bottom: 30%;
    width: 44%;
    height: 44%;
    &.chip-left {
      left: -12%;
    }
    &.chip-right {
      right: -12%;
    }
    padding: 0;
    border: none;
    border-radius: 50%;
    background: var(--coin, url("../assets/token-golem.png")) center center /
      cover no-repeat;
    box-shadow: 0 0 0 2px rgba(20, 14, 8, 0.95),
      0 3px 7px rgba(0, 0, 0, 0.8);
    cursor: pointer;
    // the coin's own click opens the character picker; the chip's opens the
    // BELIEF picker, so it must never reach the coin underneath
    pointer-events: auto;
    z-index: 4;
    transition: transform 150ms ease-out, box-shadow 150ms ease-out;

    &:hover,
    &:focus-visible {
      outline: none;
      transform: scale(1.12);
      box-shadow: 0 0 0 1.5px #{$blood}, 0 2px 6px rgba(0, 0, 0, 0.75);
    }

    // the collar's whisper of team colour, drawn as a ring on the chip's edge
    @mixin chip-collar($color) {
      &:after {
        border-color: rgba($color, 0.85);
      }
    }
    &:after {
      content: " ";
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      box-sizing: border-box;
      border: 2px solid rgba(222, 208, 172, 0.85);
      border-radius: 50%;
      pointer-events: none;
    }
    &.townsfolk {
      @include chip-collar($townsfolk);
    }
    &.outsider {
      @include chip-collar($outsider);
    }
    &.minion {
      @include chip-collar($minion);
    }
    &.demon {
      @include chip-collar($demon);
    }
    &.traveler {
      @include chip-collar($traveler);
    }

    // `.token span` makes every span in here a full-bleed 100% layer; the icon
    // wants the same box but its own fit, so it restates it rather than
    // inheriting a size meant for the coin's face.
    // The chip is a third of the coin across, so its art gets nearly the whole
    // face — at 78% the engraving read as a smudge. It also carries the big
    // coin's own lift off centre, so the two read as the same object struck at
    // two sizes.
    .belief-icon.unset {
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: PiratesBay, sans-serif;
      font-size: 120%;
      color: #d8cdb4;
      text-shadow: 0 1px 2px black;
    }

    .belief-icon {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      background-position: center 47%;
      background-repeat: no-repeat;
      background-size: 88%;
      pointer-events: none;
      filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.5));
    }
  }
}

// A shrouded seat's coin goes cold: the same wheel struck in dead metal, so
// a full grimoire reads alive-vs-dead at a glance without hunting for
// shrouds. `.player` lives in Player.vue; the coin is this component's root,
// which is what scoping keys off.
.player.dead .token {
  background-image: var(--coin-dead, url("../assets/token-golem-dead.png"));
}

// ── FT-1469 — THE TAROT-CARD FRAME (slice 1: the primary alive face) ────────
// Additive: the coin path above is untouched and stays the default. When this
// browser picks the tarot frame the coin binds `seat-frame-tarot` and the face
// becomes a PORTRAIT CARD — the role art sitting on Tarot_Blank in place of the
// round cog. `contain` keeps the card's own aspect (never stretched to a
// square); Player.vue gives `.token` the portrait BOX (this scope owns the
// FACE, that one owns the geometry).
//
// DEFERRED, and hence STOOD ASIDE here (not solved for the card shape): the
// team ring, the wake-moon / setup / reminder marks, and the curved name — all
// coin-face furniture that distorts on a tall box. Hidden so the reviewer
// judges the CARD, not its half-ported chrome; see the ticket's deferred list.
.token.seat-frame-tarot {
  background: var(--seat-frame) center center / contain no-repeat;
  // the coin's contact shadow is round; a card wants a rectangular seat
  filter: drop-shadow(0 3px 8px rgba(0, 0, 0, 0.6));

  .icon {
    // smaller and a touch high — the art sits on the card's black field,
    // inside its thin gold border (tuned by eye for this slice)
    background-size: 58%;
    background-position: center 40%;
  }

  // coin-face furniture that needs re-solving for the card shape (deferred)
  .rim,
  .marks,
  .name {
    display: none;
  }
}
</style>
